import { Box } from '@mui/material';
import { ReferenceManyField, TextField, TopToolbar, useListContext, WithRecord } from 'react-admin';

import {
  GetB2bIndividualOperationOneResponse,
  GetB2bIndividualOperationOrderResponse,
} from '@Common/types/bo/b2b/individual_operations';

import {
  b2bIndividualOperationInitiatorChoices,
  b2bIndividualOperationTypeChoices,
} from '@sh4res/bo-common/choices/individual_operations';
import {
  b2bCancellationInitiatorChoices,
  b2bCancellationReasonChoices,
  b2bOrderStatusChoices,
  orderSideChoices,
} from '@sh4res/bo-common/choices/orders';
import {
  EmptyScreen,
  ServerExportButton,
  ShowFields,
  ShowSection,
  ShowWithNavigation,
} from '@sh4res/bo-common/components';
import { DatagridTyped } from '@sh4res/bo-common/components/DatagridTyped';
import type { LogsProps } from '@sh4res/bo-common/components/Logs/LogsButton';
import { CurrencyField, IdField, SelectChipField, UTCDateField } from '@sh4res/bo-common/fields';
import { EmptyFolderSvg } from '@sh4res/bo-common/graphics';
import { useResourceBasePath } from '@sh4res/bo-common/hooks';
import { ColumnList } from '@sh4res/bo-common/utils';
import { isBoExternal } from '@sh4res/bo-common/utils/env';

import type { JSX } from 'react';

const NoOrdersYet = () => (
  <EmptyScreen
    label="resources.bo_common.b2b.individual_operations.show.orders_empty"
    svg={<EmptyFolderSvg />}
  />
);

const OrdersExportButton = ({
  individualOperationId,
  'data-testid': dataTestId,
}: {
  individualOperationId: number;
  'data-testid': string;
}) => {
  const resourceBasePath = useResourceBasePath();
  const { total } = useListContext();

  return (
    <ServerExportButton
      resource={`${resourceBasePath}individual_operations/orders`}
      disabled={total === 0}
      getBody={() => ({
        kind: 'individual_operation_orders',
        individual_operation_id: individualOperationId,
      })}
      data-testid={dataTestId}
    />
  );
};

export const B2bIndividualOperationsShow = ({
  titleActions,
  logs,
}: {
  titleActions?: JSX.Element;
  logs?: LogsProps;
} = {}) => {
  const resourceBasePath = useResourceBasePath();
  const defaultOrdersColumns = [
    'id',
    'side',
    'gross_amount',
    'entry_fee_employee_amount',
    'entry_fee_company_amount',
    'tax_amount',
    'net_amount',
    'isin',
    'instrument_id',
    'status',
    'cancellation_initiator',
    'cancellation_reason',
    'linked_order_id',
  ] as (keyof GetB2bIndividualOperationOrderResponse)[];

  const ordersColumns: ColumnList<GetB2bIndividualOperationOrderResponse> = {
    id: !isBoExternal() ? (
      <IdField
        reference={`${resourceBasePath}orders`}
        data-testid="BoExternalB2bIndividualOperationsShow_OrderIdField"
      />
    ) : (
      <TextField data-testid="BoExternalB2bIndividualOperationsShow_OrderIdTextField" />
    ),
    side: (
      <SelectChipField
        choices={orderSideChoices}
        data-testid="BoExternalB2bIndividualOperationsShow_SideField"
      />
    ),
    gross_amount: (
      <CurrencyField
        currency="EUR"
        testId="BoExternalB2bIndividualOperationsShow_GrossAmountField"
      />
    ),
    entry_fee_employee_amount: (
      <CurrencyField
        currency="EUR"
        testId="BoExternalB2bIndividualOperationsShow_EntryFeeEmployeeAmountField"
      />
    ),
    net_amount: (
      <CurrencyField currency="EUR" testId="BoExternalB2bIndividualOperationsShow_NetAmountField" />
    ),
    isin: <TextField data-testid="BoExternalB2bIndividualOperationsShow_IsinField" />,
    instrument_id: (
      <IdField
        reference="instruments"
        data-testid="BoExternalB2bIndividualOperationsShow_InstrumentIdField"
      />
    ),
    status: (
      <SelectChipField
        choices={b2bOrderStatusChoices}
        data-testid="BoExternalB2bIndividualOperationsShow_StatusField"
      />
    ),
    cancellation_initiator: (
      <SelectChipField
        choices={b2bCancellationInitiatorChoices}
        emptyText="-"
        data-testid="BoExternalB2bIndividualOperationsShow_CancellationInitiatorField"
      />
    ),
    cancellation_reason: (
      <SelectChipField
        choices={b2bCancellationReasonChoices}
        emptyText="-"
        data-testid="BoExternalB2bIndividualOperationsShow_CancellationReasonField"
      />
    ),
    equipment: <TextField data-testid="BoExternalB2bIndividualOperationsShow_EquipmentField" />,
    operation_type: (
      <SelectChipField
        choices={b2bIndividualOperationTypeChoices}
        data-testid="BoExternalB2bIndividualOperationsShow_OperationTypeField"
      />
    ),
    availability_date: (
      <UTCDateField
        showTime
        data-testid="BoExternalB2bIndividualOperationsShow_AvailabilityDateField"
      />
    ),
    nav_date: (
      <UTCDateField showTime data-testid="BoExternalB2bIndividualOperationsShow_NavDateField" />
    ),
    nav: <CurrencyField currency="EUR" testId="BoExternalB2bIndividualOperationsShow_NavField" />,
    reference_period: (
      <TextField data-testid="BoExternalB2bIndividualOperationsShow_ReferencePeriodField" />
    ),
    tax_residence_code: (
      <TextField data-testid="BoExternalB2bIndividualOperationsShow_TaxResidenceCodeField" />
    ),
    instrument_name: (
      <TextField data-testid="BoExternalB2bIndividualOperationsShow_InstrumentNameField" />
    ),
    user_currency_amount_input: (
      <CurrencyField
        currency="EUR"
        testId="BoExternalB2bIndividualOperationsShow_UserCurrencyAmountInputField"
      />
    ),
    filled_quantity: (
      <TextField data-testid="BoExternalB2bIndividualOperationsShow_FilledQuantityField" />
    ),
    entry_fee_company_amount: (
      <CurrencyField
        currency="EUR"
        testId="BoExternalB2bIndividualOperationsShow_EntryFeeCompanyAmountField"
      />
    ),
    tax_amount: (
      <CurrencyField currency="EUR" testId="BoExternalB2bIndividualOperationsShow_TaxAmountField" />
    ),
    linked_order_id: !isBoExternal() ? (
      <IdField
        reference={`${resourceBasePath}orders`}
        data-testid="BoExternalB2bIndividualOperationsShow_LinkedOrderIdField"
      />
    ) : (
      <TextField data-testid="BoExternalB2bIndividualOperationsShow_LinkedOrderIdTextField" />
    ),
    individual_operation_id: (
      <IdField
        reference={`${resourceBasePath}individual_operations`}
        data-testid="BoExternalB2bIndividualOperationsShow_IndividualOperationIdField"
      />
    ),
    identifier: <TextField data-testid="BoExternalB2bIndividualOperationsShow_IdentifierField" />,
    firstname: <TextField data-testid="BoExternalB2bIndividualOperationsShow_FirstnameField" />,
    lastname: <TextField data-testid="BoExternalB2bIndividualOperationsShow_LastnameField" />,
    registration_number: (
      <TextField data-testid="BoExternalB2bIndividualOperationsShow_RegistrationNumberField" />
    ),
    social_status: (
      <TextField data-testid="BoExternalB2bIndividualOperationsShow_SocialStatusField" />
    ),
    plan_type: <TextField data-testid="BoExternalB2bIndividualOperationsShow_PlanTypeField" />,
    mc_gross_amount: (
      <CurrencyField
        currency="EUR"
        testId="BoExternalB2bIndividualOperationsShow_McGrosstAmountField"
      />
    ),
    mc_net_amount: (
      <CurrencyField
        currency="EUR"
        testId="BoExternalB2bIndividualOperationsShow_McNetAmountField"
      />
    ),
    mc_entry_fee_employee_amount: (
      <CurrencyField
        currency="EUR"
        testId="BoExternalB2bIndividualOperationsShow_McEntryFeeEmployeeAmountField"
      />
    ),
    mc_entry_fee_company_amount: (
      <CurrencyField
        currency="EUR"
        testId="BoExternalB2bIndividualOperationsShow_McEntryFeeCompanyAmountField"
      />
    ),
    mc_tax_info: <TextField data-testid="BoExternalB2bIndividualOperationsShow_McTaxInfoField" />,
    mc_filled_quantity: (
      <TextField data-testid="BoExternalB2bIndividualOperationsShow_McFilledQuantityField" />
    ),
    mc_user_currency_amount_input: (
      <CurrencyField
        currency="EUR"
        testId="BoExternalB2bIndividualOperationsShow_McUserCurrencyAmountInputField"
      />
    ),
    mc_individual_operation_id: (
      <IdField
        reference={`${resourceBasePath}individual_operations`}
        data-testid="BoExternalB2bIndividualOperationsShow_McIndividualOperationIdField"
      />
    ),
  };

  return (
    <ShowWithNavigation titleActions={titleActions} logs={logs}>
      <ShowSection label="resources.bo_common.b2b.individual_operations.show.title">
        <ShowFields<GetB2bIndividualOperationOneResponse>
          fields={{
            equipment: (
              <TextField data-testid="BoExternalB2bIndividualOperationsShow_ShowEquipmentField" />
            ),
            type: (
              <SelectChipField
                choices={b2bIndividualOperationTypeChoices}
                data-testid="BoExternalB2bIndividualOperationsShow_ShowTypeField"
              />
            ),
            reference_period: (
              <TextField data-testid="BoExternalB2bIndividualOperationsShow_ShowReferencePeriodField" />
            ),
            status: (
              <SelectChipField
                choices={b2bOrderStatusChoices}
                data-testid="BoExternalB2bIndividualOperationsShow_ShowStatusField"
              />
            ),
            cancellation_initiator: (
              <SelectChipField
                choices={b2bCancellationInitiatorChoices}
                emptyText="-"
                data-testid="BoExternalB2bIndividualOperationsShow_ShowCancellationInitiatorField"
              />
            ),
            cancellation_reason: (
              <SelectChipField
                choices={b2bCancellationReasonChoices}
                emptyText="-"
                data-testid="BoExternalB2bIndividualOperationsShow_ShowCancellationReasonField"
              />
            ),
            total_gross_amount: (
              <CurrencyField
                currency="EUR"
                testId="BoExternalB2bIndividualOperationsShow_ShowTotalGrossAmountField"
              />
            ),
            total_entry_fee_employee_amount: (
              <CurrencyField
                currency="EUR"
                testId="BoExternalB2bIndividualOperationsShow_ShowTotalEntryFeeEmployeeAmountField"
              />
            ),
            total_net_amount: (
              <CurrencyField
                currency="EUR"
                testId="BoExternalB2bIndividualOperationsShow_ShowTotalNetAmountField"
              />
            ),
            created_at: (
              <UTCDateField
                showTime
                data-testid="BoExternalB2bIndividualOperationsShow_ShowCreatedAtField"
              />
            ),
            updated_at: (
              <UTCDateField
                showTime
                data-testid="BoExternalB2bIndividualOperationsShow_ShowUpdatedAtField"
              />
            ),
            execution_date: (
              <UTCDateField
                showTime
                data-testid="BoExternalB2bIndividualOperationsShow_ShowExecutionDateField"
              />
            ),
            b2b_employee_id: (
              <IdField
                reference={`${resourceBasePath}employees`}
                data-testid={'BoExternalB2bIndividualOperationsShow_B2bEmployeeIdField'}
              />
            ),
            b2b_company_id: !isBoExternal() ? (
              <IdField
                reference={`${resourceBasePath}companies`}
                data-testid={'BoExternalB2bIndividualOperationsShow_B2bCompanyIdField'}
              />
            ) : undefined,
            b2b_contract_id: (
              <IdField
                reference={`${resourceBasePath}contracts`}
                data-testid={'BoExternalB2bIndividualOperationsShow_IdField'}
              />
            ),
            user_id: !isBoExternal() ? (
              <IdField
                reference={'users'}
                data-testid={'BoExternalB2bIndividualOperationsShow_IdField'}
              />
            ) : undefined,
            initiator: (
              <SelectChipField
                choices={b2bIndividualOperationInitiatorChoices}
                data-testid="BoExternalB2bIndividualOperationsShow_ShowInitiatorField"
              />
            ),
          }}
        />
        <ShowFields<GetB2bIndividualOperationOneResponse>
          hideWhenEmpty
          fields={{
            bo_user_email: (
              <TextField data-testid="BoExternalB2bIndividualOperationsShow_BoUserEmailField" />
            ),
            b2b_recurring_investment_id: !isBoExternal() ? (
              <IdField
                reference={`${resourceBasePath}recurring_investments`}
                data-testid="BoExternalB2bIndividualOperationsShow_B2bRecurringInvestmentIdField"
              />
            ) : undefined,
            b2b_correction_id: !isBoExternal() ? (
              <IdField
                source="b2b_correction_id"
                // eslint-disable-next-line regex/invalid -- only for internal BO
                reference="b2b/ops_actions/corrections"
                data-testid="BoExternalB2bIndividualOperationsShow_B2bCorrectionIdField"
              />
            ) : undefined,
          }}
        />
      </ShowSection>
      <ShowSection label="resources.bo_common.b2b.individual_operations.show.orders_title">
        <WithRecord
          render={(record: GetB2bIndividualOperationOneResponse) => (
            <ReferenceManyField
              reference={`${resourceBasePath}individual_operations/orders`}
              target="individual_operation_id"
              data-testid="BoExternalB2bIndividualOperationsShow_OrdersList">
              <Box sx={{ paddingTop: '3px', paddingBottom: '3px' }}>
                <TopToolbar>
                  <OrdersExportButton
                    individualOperationId={record.id}
                    data-testid="BoExternalB2bIndividualOperationsShow_ExportButton"
                  />
                </TopToolbar>
                <DatagridTyped<GetB2bIndividualOperationOrderResponse>
                  sortableColumns={['id', 'status', 'isin', 'instrument_id']}
                  columnRegistry={ordersColumns}
                  displayedColumns={defaultOrdersColumns}
                  showCard={true}
                  bulkActionButtons={false}
                  empty={<NoOrdersYet />}
                />
              </Box>
            </ReferenceManyField>
          )}
        />
      </ShowSection>
    </ShowWithNavigation>
  );
};
